// import CardModuleMulti from "../components/CardModuleMulti";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api.js";
import MapModule from "../components/MapModule";
import Button from "@mui/material/Button";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Typography from "@mui/material/Typography";
import Buttons from "../components/Buttons";
import CustomizedDialogs from "../components/DownloadAlert";
import CustomizedDialogsInfo from "../components/InfoAlert";
import CustomizedDialogsFilter from "../components/FilterAlert";
import { usePageMetadata } from "../hooks/usePageMetadata";
import {
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  handleTap,
} from "../utils/touchHandlers";
import { handleScroll, sortTripGallery, renderCard } from "../utils/utils";
import { selectInitialTrip } from "../utils/tripSelection";

// Start building touch events

const API_URL = process.env.REACT_APP_API;

export default function Explore() {
  const [tripId, setTripId] = useState(null);
  const [photoId, setPhotoId] = useState(0);
  const [displayOption, setDisplayOption] = useState("map");
  const [openGpxDialog, setOpenGpxDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [currentTrip, setCurrentTrip] = useState(null);
  const [trips, setTrips] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false); //for full screen
  const [touchInfo, setTouchInfo] = useState({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  // const [error, setError] = useState(null);
  const [showManyTracks, setShowManyTracks] = useState(false);
  const [highPagination, setHighPagination] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [chooseByMap, setChooseByMap] = useState(false);
  const [chosenRegion, setChosenRegion] = useState("Dolomites");
  const [startYear, setStartYear] = useState(2022);
  const [endYear, setEndYear] = useState(2025);
  const [filterByYear, setFilterByYear] = useState(false);
  const [filterByRegion, setFilterByRegion] = useState(false);
  const [filterDialogMessage, setFilterDialogMessage] = useState("");
  const skipNextRead = useRef(false);

  const { tripId: tripIdParam } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tripIdParam || trips.length === 0) return;
    const targetTrip = trips.find((trip) => String(trip.id) === tripIdParam);
    if (targetTrip) {
      setTripId(targetTrip.id);
      setPolygon(targetTrip.attributes.polygon);
      setCurrentTrip(targetTrip);
    }
  }, [tripIdParam, trips]);

  useEffect(() => {
    if (!tripId) return;
    if (String(tripId) !== tripIdParam) {
      navigate(`/explore/${tripId}`, { replace: true });
    }
  }, [tripId, tripIdParam, navigate]);

  const pageMetaTitle = currentTrip
    ? `${currentTrip.attributes.cardTitle} | Mountain Biking Trails - ${chosenRegion}`
    : `Mountain Biking Trails - ${chosenRegion}`;
  const pageMetaDescription = currentTrip
    ? `View ${currentTrip.attributes.cardTitle} in ${chosenRegion} with route maps, photos, and trip details.`
    : `Explore mountain biking routes, maps and trip galleries around ${chosenRegion}.`;
  usePageMetadata(pageMetaTitle, pageMetaDescription);

  // const [page, setPage] = useState(1);

  // Lets change setPage to useRef
  const refPage = useRef(1);

  const ref = useRef(null);

  const divRef = useRef(null); //for full screen

  const [scrollTop, setScrollTop] = useState(0);

  const [polygon, setPolygon] = useState(null);

  const mapRef = useRef(null); // Reference to the map instance

  // useEffect prevents from unwanted repetitions of readCards function with the same page
  useEffect(() => {
    readCards();
  }, [refPage.current]);

  useEffect(() => {
    if (tripId !== null) {
      setPhotoId(0);
      const filteredCard = trips.filter((card) => card.id === tripId)[0];
      if (filteredCard) {
        setPolygon(filteredCard.attributes.polygon);
        setCurrentTrip(filteredCard);
      }
    }
  }, [tripId]);

  useEffect(() => {
    if (skipNextRead.current) {
      skipNextRead.current = false;
      return;
    }

    refPage.current = 1;
    setTrips([]);
    readCards();
    setPhotoId(0);
    setDisplayOption("gallery");
  }, [chosenRegion, startYear, endYear, filterByYear, filterByRegion]);

  useEffect(() => {
    if (highPagination) {
      setPageSize(200);
    } else {
      setPageSize(20);
    }
  }, [highPagination]);

  useEffect(() => {
    if (openFilterDialog) {
      setFilterDialogMessage("");
    }
  }, [openFilterDialog]);

  useEffect(() => {
    if (displayOption === "map" && mapRef.current) {
      mapRef.current.invalidateSize();
    }
  }, [displayOption]);

  const fetchTrips = async ({
    region,
    page,
    pageSize,
    filterByYear,
    filterByRegion,
    startYear,
    endYear,
  }) => {
    setIsLoading(true);

    const filtrationString = filterByRegion
      ? "&filters[trackLink][$eq]=" + region
      : "";

    const yearFilter = filterByYear
      ? `&filters[tripDate][$gte]=${startYear}-01-01&filters[tripDate][$lte]=${endYear}-12-31`
      : "";

    const string =
      "/api/cards?populate=*&sort=tripDate" +
      filtrationString +
      `&pagination[page]=${page}&pagination[pageSize]=${pageSize}` +
      yearFilter;

    const res = await api.get(string);
    return res.data.data || [];
  };

  const fetchTripById = async (tripIdToFetch) => {
    try {
      const res = await api.get(`/api/cards/${tripIdToFetch}?populate=*`);
      return res.data.data || null;
    } catch (error) {
      console.error("Failed to fetch requested trip", error);
      return null;
    }
  };

  const readCards = async () => {
    const data = await fetchTrips({
      region: chosenRegion,
      page: refPage.current,
      pageSize,
      filterByYear,
      filterByRegion,
      startYear,
      endYear,
    });

    // Note: explore.js doesn't have hasMore state, but we can add if needed

    if (refPage.current === 1) {
      setTrips(data);

      const fallbackTrip = data.length > 0 ? data[0] : null;
      const selectedTrip = selectInitialTrip({
        tripIdParam,
        trips: data,
        defaultTrip: fallbackTrip,
      });

      if (tripIdParam) {
        if (selectedTrip) {
          setTripId(selectedTrip.id);
          setCurrentTrip(selectedTrip);
          setPolygon(selectedTrip.attributes.polygon);
        } else {
          const requestedTrip = await fetchTripById(tripIdParam);
          if (requestedTrip) {
            setTrips((prevTrips) =>
              prevTrips.some(
                (trip) => String(trip.id) === String(requestedTrip.id),
              )
                ? prevTrips
                : [requestedTrip, ...prevTrips],
            );
            setTripId(requestedTrip.id);
            setCurrentTrip(requestedTrip);
            setPolygon(requestedTrip.attributes.polygon);
          } else if (fallbackTrip) {
            setTripId(fallbackTrip.id);
            setCurrentTrip(fallbackTrip);
            setPolygon(fallbackTrip.attributes.polygon);
          }
        }
      } else if (fallbackTrip) {
        setTripId(fallbackTrip.id);
        setCurrentTrip(fallbackTrip);
        setPolygon(fallbackTrip.attributes.polygon);
      }
    } else {
      setTrips((prevItems) => [...prevItems, ...data]);
    }

    setIsLoading(false);
  };

  const handleApplyFilters = async ({
    chosenRegion: region,
    startYear: newStartYear,
    endYear: newEndYear,
    filterByYear: newFilterByYear,
    filterByRegion: newFilterByRegion,
    showManyTracks: newShowManyTracks,
    highPagination: newHighPagination,
  }) => {
    setFilterDialogMessage("");

    try {
      const data = await fetchTrips({
        region,
        page: 1,
        pageSize,
        filterByYear: newFilterByYear,
        filterByRegion: newFilterByRegion,
        startYear: newStartYear,
        endYear: newEndYear,
      });

      if (data.length === 0) {
        setFilterDialogMessage("No results were found. Change selection.");
        setIsLoading(false);
        return false;
      }

      skipNextRead.current = true;
      refPage.current = 1;
      setTrips(data);
      setTripId(data[0].id);
      setPolygon(data[0].attributes.polygon);
      setChosenRegion(region);
      setStartYear(newStartYear);
      setEndYear(newEndYear);
      setFilterByYear(newFilterByYear);
      setFilterByRegion(newFilterByRegion);
      setShowManyTracks(newShowManyTracks);
      setHighPagination(newHighPagination);
      setIsLoading(false);
      setFilterDialogMessage("");
      return true;
    } catch (err) {
      setFilterDialogMessage("Unable to check filters. Try again.");
      setIsLoading(false);
      return false;
    }
  };

  const handleScrollEvent = (event) => {
    handleScroll(
      event,
      setScrollTop,
      scrollTop,
      ref,
      isLoading,
      setIsLoading,
      refPage,
    );
  };

  if (!currentTrip) return "loading...";

  const gpxFileUrl =
    API_URL + currentTrip.attributes.gpxtrack.data[0].attributes.url;

  const tripGallery = currentTrip.attributes.gallery.data;

  sortTripGallery(tripGallery);

  const nextPhoto = () => {
    if (photoId < tripGallery.length - 1) {
      setPhotoId(photoId + 1);
      setImgLoaded(true);
    }
  };

  const prevPhoto = () => {
    if (photoId > 0) {
      setPhotoId(photoId - 1);
      setImgLoaded(false);
    }
  };

  return (
    <div className="responsiveOuterBox">
      <div
        className="responsiveCardBox"
        id="box"
        ref={ref}
        onScroll={handleScrollEvent}
      >
        {chooseByMap && currentTrip ? (
          <>
            {renderCard(
              currentTrip,
              setTripId,
              setPhotoId,
              setDisplayOption,
              setTripId,
              displayOption,
            )}
            {/* Add the button below the current card */}
            <Button
              size="large"
              variant="outlined"
              onClick={() => {
                setChooseByMap(false);
              }}
              style={{ marginTop: "10px" }}
              sx={{
                color: "white",
              }}
            >
              Show more cards
            </Button>
          </>
        ) : (
          trips.map((trip) =>
            renderCard(
              trip,
              setTripId,
              setPhotoId,
              setDisplayOption,
              setTripId,
              displayOption,
            ),
          )
        )}
      </div>

      <div className="responsiveSideBox">
        <div
          style={{
            minHeight: "40px",
          }}
        >
          <Typography variant="h5" color="white" sx={{ mt: 1 }}>
            {currentTrip.attributes.cardTitle}
          </Typography>
        </div>

        {polygon && (
          <div
            className="mapBox"
            style={{
              display: displayOption === "map" ? "flex" : "none",
              flexGrow: "1",
              backgroundColor: "red",
              flexDirection: "row",
              justifyContent: "center",
            }}
          >
            <MapModule
              polygon={polygon}
              mapRef={mapRef}
              trips={trips}
              showManyTracks={showManyTracks}
              setTripId={setTripId}
              setChooseByMap={setChooseByMap}
              currentTrip={currentTrip}
              displayOption={displayOption}
            />
          </div>
        )}

        <div
          style={{
            className: "galleryBox",
            display: displayOption === "gallery" ? "flex" : "none",
            flexDirection: "column",
            flexGrow: "1",
          }}
        >
          <div
            className="newGalleryContainer"
            ref={divRef}
            onClick={() => handleTap(divRef, isFullscreen, setIsFullscreen)}
            onTouchStart={(e) => handleTouchStart(e, setTouchInfo)}
            onTouchMove={(e) => handleTouchMove(e, setTouchInfo)}
            onTouchEnd={() =>
              handleTouchEnd(
                touchInfo,
                photoId,
                setPhotoId,
                nextPhoto,
                prevPhoto,
              )
            }
            style={{
              backgroundImage:
                'url("' +
                API_URL +
                tripGallery[photoId].attributes.formats.xlarge.url +
                '")',
              flexGrow: "1",
            }}
          ></div>

          {imgLoaded && photoId < tripGallery.length - 1 && (
            <img
              className="preload-img"
              src={
                API_URL + tripGallery[photoId + 1].attributes.formats.xlarge.url
              }
              alt="Next trip"
            ></img>
          )}
          <div
            className="arrowsBox"
            style={{
              display: "flex",
              flexwrap: "wrap",
              flexDirection: "row",
              justifyContent: "space-around",
              marginTop: "2px",
            }}
          >
            <Button
              size="large"
              variant="outlined"
              startIcon={<ArrowBackIosIcon />}
              sx={{ color: "white" }}
              onClick={prevPhoto}
              disabled={photoId < 1}
            >
              BACK
            </Button>

            <Button
              size="large"
              variant="outlined"
              sx={{
                color: "gray",
              }}
            >
              {photoId + 1} / {tripGallery.length}
            </Button>

            <Button
              size="large"
              variant="outlined"
              endIcon={<ArrowForwardIosIcon />}
              sx={{
                color: "white",
              }}
              onClick={nextPhoto}
              disabled={photoId >= tripGallery.length - 1}
            >
              NEXT
            </Button>
          </div>
        </div>

        {trips.length > 0 && (
          <Buttons
            setDisplayOption={setDisplayOption}
            setOpenGpxDialog={setOpenGpxDialog}
            setOpenInfoDialog={setOpenInfoDialog}
            setOpenFilterDialog={setOpenFilterDialog}
            setChosenRegion={setChosenRegion}
            chosenRegion={chosenRegion}
            cardId={tripId}
            setMapId={setTripId}
          />
        )}

        {openGpxDialog && (
          <CustomizedDialogs
            openGpxDialog={openGpxDialog}
            setOpenGpxDialog={setOpenGpxDialog}
            gpxFileUrl={gpxFileUrl}
            cardTitle={currentTrip.attributes.cardTitle}
          />
        )}

        {openInfoDialog && (
          <CustomizedDialogsInfo
            openInfoDialog={openInfoDialog}
            setOpenInfoDialog={setOpenInfoDialog}
            tripInfo={currentTrip.attributes.tripInfo}
            cardTitle={currentTrip.attributes.cardTitle}
            difficulty={currentTrip.attributes.difficulty}
            distance={currentTrip.attributes.distance}
            heightGain={currentTrip.attributes.heightGain}
            startPoint={currentTrip.attributes.startPoint}
            region={currentTrip.attributes.trackLink}
            tripDate={currentTrip.attributes.tripDate}
          />
        )}

        {openFilterDialog && (
          <CustomizedDialogsFilter
            openFilterDialog={openFilterDialog}
            setOpenFilterDialog={setOpenFilterDialog}
            filterMessage={filterDialogMessage}
            applyFilters={handleApplyFilters}
            chosenRegion={chosenRegion}
            showManyTracks={showManyTracks}
            highPagination={highPagination}
            startYear={startYear}
            endYear={endYear}
            filterByYear={filterByYear}
            filterByRegion={filterByRegion}
          />
        )}
      </div>
    </div>
  );
}
